import React, { useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  CssBaseline,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormikProvider, useFormik } from "formik";
import { useSignInUserMutation } from "../../generated/graphql";
import { useAppDispatch } from "../../hooks/usereduxHooks";
import { loginAction } from "../../store/actionCreator/auth.creator";
import { sweetAlert } from "../../utils/sweetAlert";
import { signInValidation } from "../../validation/signinValidation";

export default function SignIn() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const [signInRequest] = useSignInUserMutation({
    onCompleted: (data) => {
      const { email, jwtToken, name, id, timestamps } = data.signInUser;
      dispatch(
        loginAction({ email, jwtToken, name, id, password: "", timestamps }),
      );
    },
    onError: (err) => {
      sweetAlert(err.message, "error");
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await signInRequest({
          variables: {
            SignInInput: {
              ...values,
            },
          },
        });
      } catch (err) {
        // AlertError(err.response.data.message);
      }
    },
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: signInValidation,
  });

  const {
    handleSubmit,
    handleBlur,
    errors,
    handleChange,
    touched,
    values,
    isSubmitting,
  } = formik;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "background.paper",

        justifyContent: "center",
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <CssBaseline />

      {/* Main Card Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: 440,
          my: "auto",
        }}
      >
        <Box
          component="img"
          src="/logo.svg"
          alt="Technove Dokandari Logo"
          sx={{
            width: { xs: 180, sm: 220 },
            maxWidth: "70vw",
            height: "auto",
            mb: 3,
            objectFit: "contain",
          }}
        />

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 2.5, sm: 3 },
            border: 1,
            borderColor: "divider",
            backgroundColor: theme.palette.background.default || "#f5f6f8",

            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            textAlign="center"
            fontWeight={700}
            color="text.primary"
            sx={{ fontSize: { xs: "1.35rem", sm: "1.6rem" }, mb: 0.5 }}
          >
            Sign in Panel
          </Typography>

          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Enter your credentials to access your account
          </Typography>

          <FormikProvider value={formik}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ mb: 2 }}>
                <TextField
                  margin="none"
                  required
                  fullWidth
                  size="medium"
                  placeholder="Email Address"
                  label="Email Address"
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.email && errors.email)}
                  inputProps={{
                    "aria-label": "Email Address",
                  }}
                />
                {touched.email && errors.email && (
                  <FormHelperText error sx={{ mt: 0.5, mx: 1 }}>
                    {errors.email}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  margin="none"
                  required
                  fullWidth
                  size="medium"
                  name="password"
                  placeholder="Password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.password && errors.password)}
                  inputProps={{
                    "aria-label": "Password",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {touched.password && errors.password && (
                  <FormHelperText error sx={{ mt: 0.5, mx: 1 }}>
                    {errors.password}
                  </FormHelperText>
                )}
              </Box>

              <LoadingButton
                type="submit"
                loading={isSubmitting}
                loadingIndicator="Signing in..."
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 1.5,
                  mb: 2.5,
                  py: 1.25,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  borderRadius: 1.5,
                }}
              >
                Sign In
              </LoadingButton>

              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                For access or account issues, please contact your administrator.
              </Typography>
            </Box>
          </FormikProvider>
        </Paper>
      </Box>

      {/* Footer Branding */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          A solution by{" "}
          <Link
            color="primary"
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.technove.com.au/"
            fontWeight={600}
          >
            Technove
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
